import { base64FromInt16, base64ToBytes } from "util/sonicVoiceApi";

// Talk to a Sonic ElevenLabs agent through the microphone, no phone line.
// The API hands out a signed WebSocket URL (the ElevenLabs key never reaches the
// browser); this module streams the mic in, plays the agent out and reports the
// conversation id so the result is finished like a phone call.

const DEFAULT_RATE = 16000;

const rateOf = (format) => {
  const match = /pcm_(\d+)/.exec(format || "");
  return match ? parseInt(match[1], 10) : DEFAULT_RATE;
};

const toPcm16 = (float32, inputRate, targetRate) => {
  const ratio = inputRate / targetRate;
  const outLen = Math.floor(float32.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i += 1) {
    const s = Math.max(-1, Math.min(1, float32[Math.floor(i * ratio)]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
};

/** Ask for the microphone before anything is spent, so a refusal costs nothing. */
export const acquireMicrophone = () =>
  navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    video: false,
  });

/**
 * Start a live session. Returns { stop }.
 * Callbacks: onConversationId(id), onLine(role, text) with role "agent" | "user",
 * onEnd() when the session closes (either side), onError().
 */
export const startBrowserVoiceSession = ({
  stream,
  signedUrl,
  dynamicVariables,
  onConversationId,
  onLine,
  onEnd,
  onError,
}) => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const micCtx = new AudioCtx();
  const playCtx = new AudioCtx();
  playCtx.resume().catch(() => {});
  const source = micCtx.createMediaStreamSource(stream);
  const processor = micCtx.createScriptProcessor(4096, 1, 1);
  const playing = new Set();
  let inputRate = DEFAULT_RATE;
  let outputRate = DEFAULT_RATE;
  let playhead = 0;
  let ready = false;
  let closed = false;

  const ws = new WebSocket(signedUrl);

  processor.onaudioprocess = (ev) => {
    if (!ready || ws.readyState !== WebSocket.OPEN) return;
    const pcm = toPcm16(ev.inputBuffer.getChannelData(0), micCtx.sampleRate, inputRate);
    ws.send(JSON.stringify({ user_audio_chunk: base64FromInt16(pcm) }));
  };
  source.connect(processor);
  processor.connect(micCtx.destination);

  const stopPlayback = () => {
    playing.forEach((node) => {
      try {
        node.stop();
      } catch (e) {
        // already stopped
      }
    });
    playing.clear();
    playhead = 0;
  };

  const play = (b64) => {
    if (!b64) return;
    const bytes = base64ToBytes(b64);
    const int16 = new Int16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.byteLength / 2));
    if (!int16.length) return;
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i += 1) float32[i] = int16[i] / 32768;
    const buffer = playCtx.createBuffer(1, float32.length, outputRate);
    buffer.copyToChannel(float32, 0);
    const node = playCtx.createBufferSource();
    node.buffer = buffer;
    node.connect(playCtx.destination);
    const startAt = Math.max(playCtx.currentTime, playhead);
    node.start(startAt);
    playhead = startAt + buffer.duration;
    playing.add(node);
    node.onended = () => playing.delete(node);
  };

  const finish = () => {
    if (closed) return;
    closed = true;
    ready = false;
    processor.disconnect();
    source.disconnect();
    stream.getTracks().forEach((track) => track.stop());
    micCtx.close().catch(() => {});
    // Let the agent's last words (its goodbye before hanging up) finish playing.
    const remaining = Math.max(0, playhead - playCtx.currentTime);
    setTimeout(() => playCtx.close().catch(() => {}), remaining * 1000 + 250);
    if (onEnd) onEnd();
  };

  ws.onopen = () => {
    ws.send(
      JSON.stringify({
        type: "conversation_initiation_client_data",
        dynamic_variables: dynamicVariables || {},
      })
    );
  };

  ws.onmessage = (ev) => {
    let msg;
    try {
      msg = JSON.parse(ev.data);
    } catch (e) {
      return;
    }
    switch (msg.type) {
      case "conversation_initiation_metadata": {
        const meta = msg.conversation_initiation_metadata_event || {};
        inputRate = rateOf(meta.user_input_audio_format);
        outputRate = rateOf(meta.agent_output_audio_format);
        ready = true;
        if (onConversationId && meta.conversation_id) onConversationId(meta.conversation_id);
        break;
      }
      case "audio":
        play(msg.audio_event && msg.audio_event.audio_base_64);
        break;
      case "interruption":
        stopPlayback();
        break;
      case "ping":
        ws.send(JSON.stringify({ type: "pong", event_id: msg.ping_event && msg.ping_event.event_id }));
        break;
      case "agent_response":
        if (onLine) onLine("agent", msg.agent_response_event && msg.agent_response_event.agent_response);
        break;
      case "user_transcript":
        if (onLine) onLine("user", msg.user_transcription_event && msg.user_transcription_event.user_transcript);
        break;
      default:
        break;
    }
  };

  ws.onerror = () => {
    if (onError) onError();
  };
  ws.onclose = finish;

  return {
    stop: () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      } else {
        finish();
      }
    },
  };
};
